import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ChefHat,
  PartyPopper,
  Salad,
  ShieldCheck,
  Leaf,
  Users,
  Clock,
  UtensilsCrossed,
  Package,
  FileText,
} from "lucide-react";
import { useSeo } from "@/components/Seo";

const HERO_IMAGE = "/api/images/8a2e452d-126c-4a8f-8307-f037195cbe0f";

const CELEBRATIONS = [
  {
    title: "Birthday parties",
    text: "Kids' birthdays and milestone celebrations at home or a venue, with crowd-pleasing starters, chaat and pasta live counters, mains and sweets sized to your guest list.",
  },
  {
    title: "Housewarming functions",
    text: "Traditional vegetarian meals for griha pravesh and housewarming gatherings, planned around your puja timings so lunch or dinner is served fresh.",
  },
  {
    title: "Family gatherings",
    text: "Get-togethers, festive lunches and weekend family dinners — simple, homely menus or fuller spreads, depending on the occasion and headcount.",
  },
  {
    title: "Anniversary celebrations",
    text: "Anniversary dinners and small family celebrations with a menu you pick dish by dish — from welcome drinks to desserts.",
  },
];

const WHY = [
  { icon: Salad, title: "Vegetarian focus", text: "A pure-vegetarian kitchen with a menu built around vegetarian starters, mains, live counters and sweets." },
  { icon: UtensilsCrossed, title: "130+ dishes to choose from", text: "Shape the menu for your celebration from a large, real menu — not a fixed thali." },
  { icon: ShieldCheck, title: "Hygienic & safe", text: "Food prepared in a hygienic kitchen with fresh ingredients." },
  { icon: Users, title: "Experienced team", text: "A team that has served family functions, weddings and large gatherings across Mumbai." },
  { icon: Clock, title: "On-time service", text: "Catering planned around your function timings, whether it's an afternoon party or an evening dinner." },
  { icon: Leaf, title: "Transparent quotations", text: "You get a written, itemised quotation to review and approve before anything is confirmed." },
];

export default function BirthdayFamilyCateringMumbai() {
  useSeo({
    title: "Birthday & Family Function Catering in Mumbai | Vegetarian Caterers | Tezz Khana",
    description:
      "Vegetarian catering for birthdays and family functions in Mumbai by Tezz Khana. Flexible menus for celebrations of every size across Mumbai, Navi Mumbai and Thane. Request a quote.",
    path: "/birthday-family-catering-mumbai",
    ogTitle: "Birthday & Family Function Catering in Mumbai | Tezz Khana",
    ogDescription:
      "Vegetarian catering for birthdays, family gatherings and small celebrations in Mumbai, with flexible menus from Tezz Khana.",
  });

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "ld-birthday-family-catering";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Birthday and family function catering",
      name: "Birthday & Family Function Catering in Mumbai",
      description:
        "Vegetarian catering in Mumbai for birthday parties, housewarming functions, family gatherings and anniversary celebrations, with flexible menus and custom quotations.",
      url: "https://tezzkhana.com/birthday-family-catering-mumbai",
      areaServed: [
        { "@type": "City", name: "Mumbai" },
        { "@type": "City", name: "Navi Mumbai" },
        { "@type": "City", name: "Thane" },
      ],
      provider: {
        "@type": "FoodEstablishment",
        name: "Tezz Khana Catering Service",
        url: "https://tezzkhana.com",
        servesCuisine: "Indian vegetarian",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Goregaon East, Mumbai",
          addressRegion: "Maharashtra",
          addressCountry: "IN",
        },
      },
    });
    document.head.appendChild(script);
    return () => {
      document.getElementById("ld-birthday-family-catering")?.remove();
    };
  }, []);

  return (
    <>
      {/* HERO */}
      <section className="container-x py-14 sm:py-20" data-testid="bfm-hero">
        <div className="grid items-center gap-10 lg:grid-cols-[1.1fr_1fr]">
          <div>
            <span className="kbd-eyebrow">
              <PartyPopper className="h-3 w-3" /> Tezz Khana Catering Service
            </span>
            <h1 className="mt-4 font-serif-display text-4xl font-semibold leading-tight text-secondary sm:text-5xl lg:text-6xl">
              Birthday &amp; Family Function Catering in Mumbai
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground">
              Thoughtfully prepared vegetarian catering for birthdays, family gatherings
              and small celebrations — because every occasion deserves great food.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/request-quote"
                data-testid="bfm-hero-quote"
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-primary px-6 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
              >
                Request a Custom Quote <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/build-your-menu"
                data-testid="bfm-hero-build"
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-border px-6 text-sm font-semibold text-secondary hover:bg-muted"
              >
                <ChefHat className="h-4 w-4" /> Build Your Menu
              </Link>
            </div>
          </div>
          <img
            src={HERO_IMAGE}
            alt="Gulab jamun sweets from the Tezz Khana vegetarian celebration menu"
            className="h-64 w-full rounded-3xl object-cover shadow-lg sm:h-80 lg:h-96"
            loading="eager"
          />
        </div>
      </section>

      {/* INTRODUCTION */}
      <section className="container-x pb-14" data-testid="bfm-intro">
        <div className="max-w-3xl space-y-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
          <p>
            Tezz Khana is a vegetarian catering service based in Goregaon East, catering
            birthdays and family functions across Mumbai. From a small birthday at home to
            a full family celebration at a hall, the menu is planned around your guest
            count, the occasion and what your family enjoys eating.
          </p>
          <p>
            There is no fixed party menu — browse the{" "}
            <Link to="/menu" className="font-semibold text-primary hover:underline">
              full menu
            </Link>
            , start from ready-made{" "}
            <Link to="/packages" className="font-semibold text-primary hover:underline">
              packages
            </Link>{" "}
            or build your own selection, and then request a customised quotation for your
            celebration.
          </p>
        </div>
      </section>

      {/* CELEBRATION TYPES */}
      <section className="container-x pb-16" data-testid="bfm-celebrations">
        <span className="kbd-eyebrow">Celebration catering options</span>
        <h2 className="mt-3 text-base font-semibold text-secondary md:text-lg">
          Celebration types we serve
        </h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {CELEBRATIONS.map((f) => (
            <div key={f.title} className="rounded-2xl border border-border/70 bg-card p-6">
              <h3 className="text-sm font-semibold text-secondary">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* MENU & FOOD OPTIONS */}
      <section className="container-x pb-16" data-testid="bfm-menu">
        <div className="rounded-3xl border border-border/70 bg-card p-8 sm:p-10">
          <span className="kbd-eyebrow">Menu & food options</span>
          <h2 className="mt-3 text-base font-semibold text-secondary md:text-lg">
            Choose from 130+ vegetarian dishes
          </h2>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            The Tezz Khana menu has everything a family celebration calls for: welcome
            drinks, fresh juices and mocktails, vegetarian starters, chaat and pasta live
            counters, veg and paneer main courses, breads, rice, and a wide range of sweets
            and desserts. Pick what suits the occasion — kids' favourites for a birthday or
            a traditional spread for a housewarming.
          </p>
          <Link
            to="/menu"
            data-testid="bfm-menu-link"
            className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
          >
            Explore the full menu <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* PACKAGES + BUILD YOUR MENU */}
      <section className="container-x grid gap-6 pb-16 lg:grid-cols-2" data-testid="bfm-packages">
        <div className="rounded-3xl border border-border/70 bg-card p-8">
          <span className="grid h-11 w-11 place-items-center rounded-xl bg-primary/10 text-primary">
            <Package className="h-5 w-5" />
          </span>
          <h2 className="mt-4 text-base font-semibold text-secondary md:text-lg">Flexible packages</h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            If you&apos;d rather start from a ready structure, Tezz Khana offers Silver,
            Gold and Platinum packages, plus a fully custom option. Each package can be
            adjusted to your celebration and guest count before you receive a quotation.
          </p>
          <Link
            to="/packages"
            data-testid="bfm-packages-link"
            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
          >
            View packages <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="rounded-3xl border border-border/70 bg-card p-8">
          <span className="grid h-11 w-11 place-items-center rounded-xl bg-primary/10 text-primary">
            <ChefHat className="h-5 w-5" />
          </span>
          <h2 className="mt-4 text-base font-semibold text-secondary md:text-lg">Build your celebration menu</h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Prefer to hand-pick every dish? Use the menu builder to select starters,
            mains, live counters and sweets dish by dish, exactly as you want them served
            at your celebration.
          </p>
          <Link
            to="/build-your-menu"
            data-testid="bfm-build-link"
            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
          >
            Start building your menu <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="container-x pb-16" data-testid="bfm-why">
        <span className="kbd-eyebrow">Why choose Tezz Khana</span>
        <h2 className="mt-3 text-base font-semibold text-secondary md:text-lg">
          Why families choose Tezz Khana for their celebrations
        </h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {WHY.map((w) => (
            <div key={w.title} className="rounded-2xl border border-border/70 bg-card p-6">
              <w.icon className="h-5 w-5 text-primary" />
              <h3 className="mt-3 text-sm font-semibold text-secondary">{w.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{w.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICE AREA */}
      <section className="container-x pb-16" data-testid="bfm-service-area">
        <div className="rounded-3xl border border-border/70 bg-card p-8 sm:p-10">
          <span className="kbd-eyebrow">Service area</span>
          <h2 className="mt-3 text-base font-semibold text-secondary md:text-lg">
            Serving celebrations across Mumbai, Navi Mumbai and Thane
          </h2>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            Based in Goregaon East, Tezz Khana caters family functions across Mumbai —
            from South Mumbai to the Western and Central suburbs — as well as Navi Mumbai
            and Thane. Share your home or venue location while requesting your quote and
            we&apos;ll plan the service around it.
          </p>
        </div>
      </section>

      {/* HOW TO REQUEST A QUOTE */}
      <section className="container-x pb-16" data-testid="bfm-steps">
        <span className="kbd-eyebrow">How it works</span>
        <h2 className="mt-3 text-base font-semibold text-secondary md:text-lg">
          How to request a celebration catering quote
        </h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {[
            { n: "1", t: "Choose your celebration requirements", d: "Tell us the occasion, date and expected guest count." },
            { n: "2", t: "Select or customise your menu", d: "Pick a package or build your own dish-by-dish selection." },
            { n: "3", t: "Request a quotation", d: "Receive an itemised quote to review, adjust and approve." },
          ].map((s) => (
            <div key={s.n} className="rounded-2xl border border-border/70 bg-card p-6">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-primary/10 text-sm font-bold text-primary">{s.n}</span>
              <h3 className="mt-3 text-sm font-semibold text-secondary">{s.t}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>
        <Link
          to="/request-quote"
          data-testid="bfm-steps-quote-link"
          className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
        >
          <FileText className="h-4 w-4" /> Go to the quote request form <ArrowRight className="h-4 w-4" />
        </Link>
      </section>

      {/* FINAL CTA */}
      <section className="bg-primary py-14 text-primary-foreground sm:py-16" data-testid="bfm-cta">
        <div className="container-x flex flex-col items-center justify-between gap-6 sm:flex-row">
          <div>
            <h2 className="font-serif-display text-2xl font-semibold sm:text-3xl">
              Planning a Birthday or Family Celebration?
            </h2>
            <p className="mt-2 max-w-xl text-sm text-primary-foreground/80">
              Tell us about your event and catering requirements, and we&apos;ll help you
              plan a suitable menu.
            </p>
          </div>
          <div className="flex flex-col items-center gap-3 sm:flex-row">
            <Link
              to="/request-quote"
              data-testid="bfm-cta-quote"
              className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-accent px-6 text-sm font-semibold text-secondary transition-transform hover:-translate-y-0.5"
            >
              Request Custom Quote <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/build-your-menu"
              data-testid="bfm-cta-build"
              className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-primary-foreground/40 px-6 text-sm font-semibold text-primary-foreground hover:bg-primary-foreground/10"
            >
              <ChefHat className="h-4 w-4" /> Build Your Menu
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
